import * as React from 'react';
import moment, { Moment } from 'moment';
import { MapPageFormFilter } from './Filters';
import { DatePicker, Spin } from 'antd';
import { useReopenOnOrientation } from '../../../Helper/OrientationHelper';
import { plantingDay } from '../../../Helper/DateHelper';

// The calendar's weeks run Monday to Sunday (antd reads the week start from the moment locale it
// renders with). `doy: 4` keeps week 1 as the one holding the first Thursday, i.e. ISO weeks
moment.updateLocale('en', { week: { dow: 1, doy: 4 } });

const { RangePicker } = DatePicker;

interface DateRangeFilterProps extends MapPageFormFilter {
  /** Days (local `YYYY-MM-DD`) with at least one tree planted and matching the applied filters */
  plantedDates?: string[];
  /** True while those days are still being read: the month is covered by a spinner */
  plantedDatesLoading?: boolean;
  /** Mobile: read-only inputs, so tapping the field opens the panel but no virtual keyboard */
  inputReadOnly?: boolean;
}

export default function DateRangeFilter(props: DateRangeFilterProps) {
  const { value: formValue, onChange: updateForm, isLoading, plantedDates, plantedDatesLoading, inputReadOnly } = props;
  const [value, setValue] = React.useState<Moment[]|undefined>(formValue);
  const planted = React.useMemo(() => new Set(plantedDates ?? []), [plantedDates]);
  const [open, setOpen] = React.useState(false);
  // Turning the phone re-renders an opened select lis or calendar
  useReopenOnOrientation(open, setOpen);
  const triggerRef = React.useRef<HTMLElement | null>(null);
  const panelRef = React.useRef<HTMLDivElement | null>(null);
  const touchStart = React.useRef<{ x: number; y: number } | null>(null);
  const swipedAt = React.useRef<number>(0);

  // Mobile: the calendar is drawn as wide as the Dates field
  const syncDropdownWidth = React.useCallback(() => {
    const trigger = triggerRef.current;
    const dropdown = trigger?.parentElement?.querySelector('.ant-picker-dropdown') as HTMLElement | null;
    if (!trigger || !dropdown || !window.matchMedia('(max-width: 991px)').matches) {
      return;
    }
    dropdown.style.width = `${trigger.getBoundingClientRect().width}px`;
  }, []);

  // Swiping the month moves it to the next or previous one
  const onPanelTouchStart = (event: React.TouchEvent) => {
    const touch = event.touches[0];
    touchStart.current = touch ? { x: touch.clientX, y: touch.clientY } : null;
  };

  const onPanelTouchEnd = (event: React.TouchEvent) => {
    const start = touchStart.current;
    touchStart.current = null;
    const touch = event.changedTouches[0];
    if (!start || !touch) {
      return;
    }

    // Leftwards (dx > 0) goes forward in time, rightwards goes back
    const dx = start.x - touch.clientX;
    const dy = start.y - touch.clientY;
    if (Math.abs(dx) < 40 || Math.abs(dx) <= Math.abs(dy)) {
      return;
    }

    swipedAt.current = Date.now();
    const arrow = dx > 0 ? '.ant-picker-header-next-btn' : '.ant-picker-header-prev-btn';
    panelRef.current?.querySelector<HTMLElement>(arrow)?.click();
  };

  // A swipe can end over a day cell: the browser usually withholds the click for a moved touch,
  // this makes sure of it right after a swipe
  const onPanelClickCapture = (event: React.MouseEvent) => {
    const target = event.target as HTMLElement;
    if (Date.now() - swipedAt.current < 350 && target.closest('.ant-picker-cell')) {
      event.stopPropagation();
      event.preventDefault();
    }
  };

  // A press anywhere outside the calendar closes it on the first try. rc-picker closes on a `mousedown` it
  // listens for itself; after a swipe that listener can miss the tap, so the calendar is closed here instead
  React.useEffect(() => {
    if (!open) {
      return;
    }

    const onPointerDown = (event: PointerEvent) => {
      const target = event.target as Node;
      const dropdown = triggerRef.current?.parentElement?.querySelector('.ant-picker-dropdown');
      if (dropdown?.contains(target) || triggerRef.current?.contains(target)) {
        return;
      }
      setOpen(false);
    };

    document.addEventListener('pointerdown', onPointerDown, true);
    return () => document.removeEventListener('pointerdown', onPointerDown, true);
  }, [open]);

  return <RangePicker
    disabled={isLoading}
    placement={'bottomLeft'}
    inputReadOnly={inputReadOnly}
    // One month at a time: the second panel is hidden and the remaining one centred by `picker-single-month`
    dropdownClassName="picker-single-month"
    // Anchored to the input so the popup can be centred on it (see form.less)
    getPopupContainer={(trigger) => {
      triggerRef.current = trigger as HTMLElement;
      return trigger.parentElement as HTMLElement;
    }}
    open={open}
    onOpenChange={(next: boolean) => {
      setOpen(next);
      if (next) {
        window.requestAnimationFrame(syncDropdownWidth);
      }
    }}
    // Day-first everywhere, in the inputs as well as in the panel header
    format="DD/MM/YYYY"
    value={formValue || value}
    allowEmpty={[true, true]}
    // Same node tree with and without the overlay: a changing layout would remount the panel
    panelRender={(node: React.ReactNode) => (
      <div
        ref={panelRef}
        className={`picker-panel${plantedDatesLoading ? ' picker-panel-loading' : ''}`}
        onTouchStart={onPanelTouchStart}
        onTouchEnd={onPanelTouchEnd}
        onClickCapture={onPanelClickCapture}
      >
        {node}
        <span className="picker-panel-spinner"><Spin /></span>
      </div>
    )}
    // Replaces the whole cell content, so the inner div has to be drawn here as well
    dateRender={(current: Moment) => (
      <div className={`ant-picker-cell-inner${planted.has(plantingDay(current.toDate())) ? ' date-planted' : ''}`}>
        {current.date()}
      </div>
    )}
    // `allowEmpty` makes either end nullable, so the value cannot be typed as a Moment pair
    onChange={(dates: any) => {
      setValue(dates);
      updateForm && updateForm(dates);
    }}
  />
}